import { Component, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription } from "rxjs";
import { DataTableDirective } from "angular-datatables";
import { UserDataStoreService } from "../services/user-data-store.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MerchantViewModel } from "../models/merchant-view-model";
import { MerchantService } from "../services/merchant.service";
import { MerchantViewComponent } from "./merchant-view/merchant-view.component";
import { MerchantDTViewModel } from "../models/merchant-dt-view-model";
import { Router } from '@angular/router';
import { PermissionEnum, AccessPolicyEnum } from "../services/guards/permissions";
import { AuthenticationService } from "../services/authentication.service";
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { HttpClient } from '@angular/common/http';
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { DataTablesResponseViewModel } from '../models/data-tables-response-view-model';
import { environment } from '../../environments/environment';
import { SearchFilterModel } from '../models/search-filter-model';
import { ConfirmationDialogComponent } from '../partials/confirmation-dialog/confirmation-dialog.component';
import { TerminalService } from '../services/terminal.service';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';

@Component({
  selector: 'app-merchant-management',
  templateUrl: './merchant-management.component.html',
  styleUrls: ['./merchant-management.component.scss']
})
export class MerchantManagementComponent implements OnInit {
  public dtTrigger: Subject<boolean> = new Subject();
  public merchants: MerchantDTViewModel[] = [];
  public searchDebounce = new Subject<string>();


  @ViewChild(DataTableDirective)
  public dtElement: DataTableDirective;

  public canAdd = false;
  public canEdit = false;
  public canView = false;
  public isAdmin = false;
  public canDelete = false;
  public nameLocationSearch = '';
  public merchantIdSearch = '';

  public editView: string;
  private searchEndpoint = `${environment.apiUrl}/api/merchant/Search`;
  public globalSearch = false;

  constructor(
    private _httpClient: HttpClient,
    private _router: Router,
    private _userDataStore: UserDataStoreService,
    private _authenticationService: AuthenticationService,
    private _modalService: NgbModal,
    private _merchantService: MerchantService,
    private _ngxUiLoaderService: NgxUiLoaderService,
    private _terminalService: TerminalService,
    private _toastService: ToastrService,
    private _location: Location)
    {
    this.canAdd = this._userDataStore.hasRequiredPermissions([PermissionEnum.AddMerchant], [AccessPolicyEnum.ViewMerchant]);
    this.canEdit = this._userDataStore.hasRequiredPermissions([PermissionEnum.EditMerchant], [AccessPolicyEnum.ViewMerchant]);
    this.canView = this._userDataStore.hasRequiredPermissions([PermissionEnum.ViewMerchant], [AccessPolicyEnum.ViewMerchant]);
    this.isAdmin = this._userDataStore.hasRequiredPermissions([], [AccessPolicyEnum.GlobalUserSearch]);
    
    this.canDelete = this._userDataStore.hasRequiredPermissions([PermissionEnum.DeleteMerchant], [AccessPolicyEnum.DeleteMerchant]);
    
    if (!this.canEdit) {
      if (this.canView) {
        this.editView = "View";
      }
    }
    else {
      this.editView = "Edit";
    }
  }

  ngOnInit() {
    
    if(sessionStorage.getItem('merchantGlobalSearch') === 'true') {
      this.globalSearch = true;
    }
    this.setGlobalSearch(null);
  }

  public async ngAfterViewInit(): Promise<void> {
    
    this.dtTrigger.next();
  }

  public setupFilters(): void {
    
    const searchColumns = this.columns.map((col) => {
      if (!!(col).search) {
        return { search: (col).search.value, regex: (col).search.regex };
      } else {
        return null;
      }
    });

    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      let i = 0;
      for (const item of searchColumns) {
        if (item) {
          dtInstance.column(i).search(item.search, item.regex);
        }
        i++;
      }
    })
  }

  public columns: any[] = [
    { data: 'merchantNumber', title: 'Merchant Id', visible: true, searchable: true, search: { value: this.merchantIdSearch, regex: true } },
    { data: 'nameLocation', title: 'Location', visible: true, searchable: true, search: { value: this.nameLocationSearch, regex: true } },
  ];

  private searchColumns = this.columns.map((col) => {
    if (!!col.search) {
      return { search: col.search.value, regex: col.search.regex };
    } else {
      return null;
    }
  });

  public dtOptions: DataTables.Settings = {
    pageLength: 10,
    serverSide: true,
    processing: true,
    ajax: (dataTablesParameters: any, callback) => {
      this._httpClient
        .post<DataTablesResponseViewModel>(
          this.searchEndpoint,
          dataTablesParameters, {}
      ).subscribe(resp => {

        this.merchants = resp.data;

          callback({
            recordsTotal: resp.recordsTotal,
            recordsFiltered: resp.recordsFiltered,
            data: []
          });
        });
    },
    columns: this.columns,
    searchCols: this.searchColumns
  };

  public get displayColumns() {
    return this.dtOptions.columns.filter(x => x.visible != false);
  }

  public setGlobalSearch(event: any) {
    if (event !== null) {
      if (event.target.checked) {
        this.globalSearch = true;
      } else {
        this.globalSearch = false;
      }
    } 
    
    if (this.globalSearch) {
      this.searchEndpoint = `${environment.apiUrl}/api/merchant/Search-All`;
      sessionStorage.setItem('merchantGlobalSearch', 'true');
    } else {
      this.searchEndpoint = `${environment.apiUrl}/api/merchant/Search`;
      sessionStorage.setItem('merchantGlobalSearch', 'false');
    }

    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      dtInstance.ajax.reload()
    });
  }

  public async  addMerchant(): Promise<void> {
    if(this.globalSearch) {
      this.globalSearch = false;
      this.setGlobalSearch(null);
    }
    this._router.navigate(['management', 'merchant', 'new']);
  }

  public async editMerchant(merchant: MerchantViewModel): Promise<void> {
    this._router.navigate(['management', 'merchant', merchant.merchantNumber]);
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  public async determineDeleteDialog(merchant: MerchantViewModel) {

    this._ngxUiLoaderService.start();
    var terminalDetails = await this._terminalService.GetTerminalsByMerchantNumber(merchant.merchantNumber);
    this._ngxUiLoaderService.stop();
    if (terminalDetails["result"] == null) {
      await this.openDeleteConfirmationDialog(merchant);
    } else {
      var tids = terminalDetails["result"].map(td => 'TID: ' + td.id)
      await this.openUsedDeleteFailureConfirmationDialog(tids);
    }
  }

  public async openDeleteConfirmationDialog(merchant: MerchantViewModel) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Merchant removal confirmation"
    dialogRef.componentInstance.message = "Are you sure you want to delete the selected Merchant? <br/><p style='color:red'> Please note that this merchant cannot be recovered once deleted.</p>"

    const result = await dialogRef.result;
    if (result) {
      await this.deleteMerchant(merchant);
    }
  }

  public async deleteMerchant(merchant: MerchantViewModel): Promise<void> {
    this.canDelete = this._userDataStore.hasRequiredPermissions([PermissionEnum.DeleteMerchant], []);
    if (!this.canDelete) {
      this._toastService.error("You do not have the required permissions to delete a merchant.", "error");
    }
    else {
      this._ngxUiLoaderService.start();
      const result = await this._merchantService.DeleteMerchant(merchant.merchantNumber);
      this._ngxUiLoaderService.stop();
      if (result.success === true) {
        this._toastService.success(result.message, "Success");
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
          dtInstance.ajax.reload()
        });
      } else {
        this._toastService.error(result.message, "Error");
      }
    }
  }

  public async openUsedDeleteFailureConfirmationDialog(terminals: string[]) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.acceptButtonText = "OK"
    dialogRef.componentInstance.title = "Merchant cannot be removed"
    dialogRef.componentInstance.message = "This Merchant cannot be deleted as there are still terminals linked to it, kindly delete the following terminals and try again.<br/><br/><b>TID :</b>"
    dialogRef.componentInstance.listMessage = terminals;
    dialogRef.componentInstance.listMessageHtml;
  }


}
